import { useEffect, useState, type JSX } from 'react';

import { css } from '@compiled/react';
import { UniformSlot } from '@uniformdev/canvas-react';

import { useExperiment } from '@/utils/experiment/use-experiment';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';
import { isProduction } from '@/utils/urls';

import ExperimentOverrideControls, { type ExperimentVariant } from './experiment-override-controls';
import {
	isTreatmentValue,
	useUniformExperimentConfig,
	type UniformExperimentConfigParameters,
} from './use-uniform-experiment-config';

export interface UniformExperimentWithoutManifestProps extends UniformExperimentConfigParameters {
	postExperimentationMode?: boolean;
	override?: ExperimentVariant;
}

type ManualOverrideState = ExperimentVariant | null;

const WRAPPER_STYLES = css({
	position: 'relative',
});

const renderSlot = (variant: ExperimentVariant): JSX.Element => <UniformSlot name={variant} />;

const getActiveVariant = ({
	manualOverride,
	propOverride,
	enrolledVariant,
}: {
	manualOverride: ManualOverrideState;
	propOverride?: ExperimentVariant;
	enrolledVariant: ExperimentVariant;
}): ExperimentVariant => {
	if (manualOverride) {
		return manualOverride;
	}

	if (propOverride) {
		return propOverride;
	}

	return enrolledVariant;
};

const UniformExperimentWithoutManifest = (props: UniformExperimentWithoutManifestProps) => {
	const {
		experimentId,
		parameterName,
		valueType,
		treatmentValues,
		targetedPathnames,
		audience,
		deferExposure,
		override,
		postExperimentationMode,
	} = props;

	const isProd = isProduction();

	const experiment = useUniformExperimentConfig({
		experimentId,
		parameterName,
		valueType,
		treatmentValues,
		targetedPathnames,
		audience,
		deferExposure: deferExposure || postExperimentationMode,
	});
	const experimentStatus = useExperiment(experiment);

	const [manualOverride, setManualOverride] = useState<ManualOverrideState>(null);

	useEffect(() => {
		setManualOverride(null);
	}, [experimentId]);

	const enrolledVariant: ExperimentVariant =
		experimentStatus.type === 'enrolled' &&
		isTreatmentValue({ value: experimentStatus.value, valueType, treatmentValues })
			? 'treatment'
			: 'control';

	if (postExperimentationMode) {
		return (
			<FeatureExposed experimentKey={experimentId} group="Test">
				<UniformSlot name="treatment" />
			</FeatureExposed>
		);
	}

	if (!isProd) {
		const activeVariant = getActiveVariant({
			manualOverride,
			propOverride: override,
			enrolledVariant,
		});

		return (
			<div css={WRAPPER_STYLES}>
				<ExperimentOverrideControls
					experimentId={experimentId}
					selectedVariant={manualOverride}
					setSelectedVariant={setManualOverride}
				/>
				{renderSlot(activeVariant)}
			</div>
		);
	}

	return renderSlot(enrolledVariant);
};

export default UniformExperimentWithoutManifest;
